import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { InstructorAuthProvider } from './context/InstructorAuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import BookLesson from './pages/BookLesson';
import BecomeInstructor from './pages/BecomeInstructor';
import InstructorSignup from './pages/InstructorSignup';
import LeaveReview from './pages/LeaveReview';
import ADIFormContainer from './components/ADIForm/ADIFormContainer';
import ApplicationSubmitted from './pages/ApplicationSubmitted';
import About from './pages/About';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import InstructorTerms from './pages/InstructorTerms';
import AdminLogin from './components/Admin/AdminLogin';
import AdminDashboard from './components/Admin/AdminDashboard';
import ApplicationsList from './components/Admin/ApplicationsList';
import CMSEditor from './components/Admin/CMSEditor';
import StudentRequests from './components/Admin/StudentRequests';
import Students from './components/Admin/Students';
import Revenue from './components/Admin/Revenue';
import Payments from './components/Admin/Payments';
import Referrals from './components/Admin/Referrals';
import AdminSettings from './components/Admin/AdminSettings';
import AdminInstructors from './components/Admin/AdminInstructors';
import AdminReviews from './components/Admin/AdminReviews';
import AdminRefunds from './components/Admin/AdminRefunds';
import AdminContacts from './components/Admin/AdminContacts';
import AdminResetPassword from './components/Admin/AdminResetPassword';
import TestCentres from './components/Admin/TestCentres';
import ProtectedRoute from './components/ProtectedRoute';
import InstructorLogin from './components/InstructorPortal/InstructorLogin';
import InstructorPortal from './components/InstructorPortal/InstructorPortal';
import InstructorResetPassword from './components/InstructorPortal/InstructorResetPassword';
import InstructorProtectedRoute from './components/InstructorPortal/InstructorProtectedRoute';
import PaymentSuccess from './pages/PaymentSuccess';
import RefundRequest from './pages/RefundRequest';
import ConfirmBooking from './pages/ConfirmBooking';
import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// WhatsApp floating CTA button — shown on all public pages
function WhatsAppButton() {
  const WHATSAPP_NUMBER = '447534738009';
  const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;
  return (
    <a
      id="whatsapp-cta"
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      title="Chat on WhatsApp: +44 7534 738009"
      style={{
        position: 'fixed',
        bottom: '1.75rem',
        right: '1.75rem',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 58,
        height: 58,
        borderRadius: '50%',
        background: '#25D366',
        boxShadow: '0 4px 20px rgba(37,211,102,0.45)',
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s',
        animation: 'wa-pulse 2.5s ease-in-out infinite',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.12)';
        e.currentTarget.style.boxShadow = '0 6px 28px rgba(37,211,102,0.6)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,211,102,0.45)';
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="30" height="30" fill="#fff">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.473 2.027 7.775L0 32l8.474-2.002A15.946 15.946 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.3 13.3 0 01-6.787-1.857l-.486-.29-5.029 1.188 1.21-4.907-.317-.506A13.267 13.267 0 012.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.28-9.88c-.4-.2-2.365-1.167-2.732-1.3-.367-.133-.633-.2-.9.2s-1.032 1.3-1.265 1.567c-.233.267-.467.3-.867.1-.4-.2-1.688-.623-3.215-1.983-1.188-1.06-1.99-2.37-2.223-2.77-.233-.4-.025-.617.175-.817.18-.18.4-.467.6-.7.2-.233.267-.4.4-.667.133-.267.067-.5-.033-.7-.1-.2-.9-2.167-1.233-2.967-.324-.78-.653-.674-.9-.686l-.767-.013c-.267 0-.7.1-1.067.5-.367.4-1.4 1.367-1.4 3.333s1.433 3.867 1.633 4.133c.2.267 2.82 4.307 6.833 6.04.956.412 1.7.659 2.282.844.958.306 1.832.263 2.52.16.769-.115 2.365-.967 2.699-1.9.333-.933.333-1.733.233-1.9-.1-.167-.367-.267-.767-.467z"/>
      </svg>
      <style>{`
        @keyframes wa-pulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(37,211,102,0.45); }
          50% { box-shadow: 0 4px 30px rgba(37,211,102,0.75), 0 0 0 8px rgba(37,211,102,0.15); }
        }
      `}</style>
    </a>
  );
}

function App() {
  return (
    <AuthProvider>
      <InstructorAuthProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Routes>
          {/* Admin Routes — discreet URL prefix, no Header/Footer */}
            <Route path="/ft-portal/login" element={<AdminLogin />} />
            <Route path="/ft-portal/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/ft-portal/applications" element={<ProtectedRoute><ApplicationsList /></ProtectedRoute>} />
            <Route path="/ft-portal/applications/:id" element={<ProtectedRoute><ApplicationsList /></ProtectedRoute>} />
            <Route path="/ft-portal/instructors" element={<ProtectedRoute><AdminInstructors /></ProtectedRoute>} />
            <Route path="/ft-portal/student-requests" element={<ProtectedRoute><StudentRequests /></ProtectedRoute>} />
            <Route path="/ft-portal/students" element={<ProtectedRoute><Students /></ProtectedRoute>} />
            <Route path="/ft-portal/revenue" element={<ProtectedRoute><Revenue /></ProtectedRoute>} />
            <Route path="/ft-portal/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
            <Route path="/ft-portal/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
            <Route path="/ft-portal/cms" element={<ProtectedRoute><CMSEditor /></ProtectedRoute>} />
            <Route path="/ft-portal/cms/:page" element={<ProtectedRoute><CMSEditor /></ProtectedRoute>} />
            <Route path="/ft-portal/test-centres" element={<ProtectedRoute><TestCentres /></ProtectedRoute>} />
            <Route path="/ft-portal/reviews" element={<ProtectedRoute><AdminReviews /></ProtectedRoute>} />
            <Route path="/ft-portal/refunds" element={<ProtectedRoute><AdminRefunds /></ProtectedRoute>} />
            <Route path="/ft-portal/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
            <Route path="/ft-portal/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />

            {/* Instructor Portal — separate auth, no Header/Footer */}
            <Route path="/instructor-login" element={<InstructorLogin />} />
            <Route path="/instructor-reset-password" element={<InstructorResetPassword />} />
            <Route path="/ft-portal/reset-password" element={<AdminResetPassword />} />
            <Route path="/instructor-portal" element={<InstructorProtectedRoute><InstructorPortal /></InstructorProtectedRoute>} />

            {/* Public Routes - With Header/Footer */}
            <Route path="*" element={
              <>
                <Header />
                <main>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/book-lesson" element={<BookLesson />} />
                    <Route path="/become-instructor" element={<BecomeInstructor />} />
                    <Route path="/apply-signup" element={<InstructorSignup />} />
                    <Route path="/leave-review" element={<LeaveReview />} />
                    <Route path="/apply" element={<ADIFormContainer />} />
                    <Route path="/application-submitted" element={<ApplicationSubmitted />} />
                    <Route path="/payment-success" element={<PaymentSuccess />} />
                    <Route path="/confirm-booking" element={<ConfirmBooking />} />
                    <Route path="/refund-request" element={<RefundRequest />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/instructor-terms" element={<InstructorTerms />} />
                  </Routes>
                </main>
                <WhatsAppButton />
                <Footer />
              </>
            } />
          </Routes>
        </div>
      </Router>
      </InstructorAuthProvider>
    </AuthProvider>
  );
}

export default App;
